import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { reports, accounts, type AccountCardRow } from '@/api/documents';
import { orgs } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DatePopover from '@/components/DatePopover';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DOC_TYPE_LABELS: Record<string, string> = {
  'doc.manual_operations': 'Бух. довідка',
  'doc.goods_receipts': 'Надходження',
  'doc.goods_sales': 'Реалізація',
  'doc.bank_receipts': 'Банк (вхід)',
  'doc.bank_payments': 'Банк (вихід)',
  'doc.cash_receipts': 'ПКО',
  'doc.cash_payments': 'РКО',
};

export default function AccountCardPage() {
  const { t } = useTranslation();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const accountsQ = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accounts.list({ only_postable: true, limit: 600 }),
    staleTime: 60 * 60 * 1000,
  });

  const [orgId, setOrgId] = useState('');
  const [accountCode, setAccountCode] = useState('631');
  const [from, setFrom] = useState<string>('2026-02-01T00:00:00+02:00');
  const [to, setTo] = useState<string>('2026-03-01T00:00:00+02:00');

  if (!orgId && orgList.data?.items[0]) setOrgId(orgList.data.items[0].id);

  const card = useQuery({
    queryKey: ['account-card', orgId, accountCode, from, to],
    queryFn: () => reports.accountCard({
      organization_id: orgId,
      account_code: accountCode,
      date_from: from,
      date_to: to,
    }),
    enabled: !!orgId && !!accountCode,
  });

  const cols = useMemo<ColumnDef<AccountCardRow>[]>(() => [
    {
      key: 'entry_date', header: t('ac.date'), width: 110,
      sortValue: (r) => r.entry_date ?? '',
      cell: (r) => r.entry_date ? new Date(r.entry_date).toLocaleDateString('uk-UA') : '',
    },
    {
      key: 'document_type', header: t('ac.doc_type'), width: 130,
      sortValue: (r) => DOC_TYPE_LABELS[r.document_type] ?? r.document_type ?? '',
      cell: (r) => DOC_TYPE_LABELS[r.document_type] ?? r.document_type,
    },
    {
      key: 'document_number', header: t('ac.doc_number'), width: 140,
      sortValue: (r) => r.document_number ?? '',
      cell: (r) => r.document_number,
    },
    {
      key: 'side', header: t('ac.side'), width: 70, align: 'center',
      sortValue: (r) => r.side ?? '',
      cell: (r) => (
        <span style={{ fontWeight: 600, color: r.side === 'DT' ? '#15803d' : '#b91c1c' }}>
          {r.side}
        </span>
      ),
    },
    {
      key: 'corr_account_code', header: t('ac.corr'), width: 90,
      sortValue: (r) => r.corr_account_code ?? '',
      cell: (r) => r.corr_account_code,
    },
    {
      key: 'content', header: t('ac.content'),
      sortValue: (r) => r.content ?? '',
      cell: (r) => r.content,
    },
    {
      key: 'amount_dt', header: t('ac.amount_dt'), width: 130, align: 'right',
      sortValue: (r) => Number(r.amount_dt),
      cell: (r) => Number(r.amount_dt) > 0 ? fmt(r.amount_dt) : '',
    },
    {
      key: 'amount_cr', header: t('ac.amount_cr'), width: 130, align: 'right',
      sortValue: (r) => Number(r.amount_cr),
      cell: (r) => Number(r.amount_cr) > 0 ? fmt(r.amount_cr) : '',
    },
    {
      key: 'running_balance', header: t('ac.running_balance'), width: 140, align: 'right',
      sortValue: (r) => Number(r.running_balance),
      cell: (r) => (
        <span style={{ fontWeight: 600, color: Number(r.running_balance) < 0 ? '#b91c1c' : '#15803d' }}>
          {fmt(r.running_balance)}
        </span>
      ),
    },
  ], [t]);

  const totals = card.data?.totals;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{t('ac.title')}</h2>
        <button onClick={() => card.refetch()}>{t('tb.refresh')}</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('tb.organization')}</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t('ac.account')}</label>
          <select value={accountCode} onChange={(e) => setAccountCode(e.target.value)}>
            {accountsQ.data?.map((a) => (
              <option key={a.id} value={a.code}>{a.code} — {a.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t('tb.from')}</label>
          <DatePopover withTime value={from.slice(0, 16)} onChange={(v) => setFrom(v)} />
        </div>
        <div className="field">
          <label>{t('tb.to')}</label>
          <DatePopover withTime value={to.slice(0, 16)} onChange={(v) => setTo(v)} />
        </div>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <DataTable<AccountCardRow>
          rows={card.data?.rows ?? []}
          columns={cols}
          rowKey={(r) => `${r.entry_date}|${r.document_type}|${r.document_number ?? ''}|${r.side}|${r.corr_account_code}|${r.amount_dt}|${r.amount_cr}|${r.running_balance}`}
          loading={card.isLoading}
          emptyText="Немає даних."
        />
      </div>

      {totals && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.75rem', fontWeight: 600 }}>
          <span>{t('ac.account')}: <strong>{accountCode}</strong></span>
          <span>
            {t('ac.turnover_dt')}: {fmt(totals.turnover_dt)} ·{' '}
            {t('ac.turnover_cr')}: {fmt(totals.turnover_cr)}
          </span>
        </div>
      )}
    </div>
  );
}
